import { create } from 'zustand';
import { api } from '@/core/api/apiClient';
import type { Assignee, ContentTag, StandardTag } from '@cg-projects/shared';

interface LookupState {
  assignees: Assignee[];
  contentTags: ContentTag[];
  standardTags: StandardTag[];
  loaded: boolean;
  loading: boolean;
  load: () => Promise<void>;
  /** Force-refetch assignees (after a profile-photo upload). */
  reloadAssignees: () => Promise<void>;
  /** Force-refetch content tags (after one is added or removed, v1.91.0). */
  reloadContentTags: () => Promise<void>;
  /** Point one assignee at a new (cache-busted) avatar URL so it re-renders now. */
  setAvatarUrl: (uid: number, avatarUrl: string | null) => void;
}

export const useLookupStore = create<LookupState>()((set, get) => ({
  assignees: [],
  contentTags: [],
  standardTags: [],
  loaded: false,
  loading: false,

  load: async () => {
    if (get().loaded || get().loading) return;
    set({ loading: true });
    try {
      const [a, ct, st] = await Promise.all([
        api.get<{ assignees: Assignee[] }>('/assignees'),
        api.get<{ tags: ContentTag[] }>('/tags/content'),
        api.get<{ tags: StandardTag[] }>('/tags/standard'),
      ]);
      set({ assignees: a.assignees, contentTags: ct.tags, standardTags: st.tags, loaded: true, loading: false });
    } catch {
      set({ loading: false });
    }
  },

  reloadAssignees: async () => {
    try {
      const { assignees } = await api.get<{ assignees: Assignee[] }>('/assignees');
      set({ assignees });
    } catch { /* keep current */ }
  },

  reloadContentTags: async () => {
    try {
      const { tags } = await api.get<{ tags: ContentTag[] }>('/tags/content');
      set({ contentTags: tags });
    } catch { /* keep current */ }
  },

  setAvatarUrl: (uid, avatarUrl) =>
    set((s) => ({ assignees: s.assignees.map((a) => (a.id === uid ? { ...a, avatarUrl } : a)) })),
}));
